import { EditableProvider } from '@/components/Editable';
import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import {
  SKELETON_GAP_XS,
  SKELETON_HEIGHT_XS,
} from '@/components/SkeletonLoading/constants';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { useReceivedInvoiceSearch } from '@/context/services/invoice/ReceivedInvoice.service';
import { ReceivedInvoiceDetail } from '@/context/services/invoice/type';
import { Content } from '@/features/invoices/components/Details/Components/Content';
import { TimestampStatusProvider } from '@/features/invoices/components/Details/Components/Context/TimestampStatus';
import { Loading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './Details.module.scss';

const cx = classnames.bind(styles);

export type Props = {
  invoiceId: string;
  setData: Dispatch<SetStateAction<ReceivedInvoiceDetail | null>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

const InnerDetails: FC<Props> = memo(({ invoiceId, setData, setIsLoading }) => {
  const { data } = useReceivedInvoiceSearch(invoiceId);
  useEffect(() => {
    if (data) {
      setData(data);
      setIsLoading(false);
    }
  }, [data, setData, setIsLoading]);
  return (
    <TimestampStatusProvider value={data.timestamp_status}>
      <EditableProvider
        name='ReceivedInvoiceEdit'
        isEditable={data.invoice_report ? false : true}
      >
        <Content splitView data={data} />
      </EditableProvider>
    </TimestampStatusProvider>
  );
});
InnerDetails.displayName = 'InnerDetails';

const LoadingDetails: FC = memo(() => {
  const LOADING_HEIGHT = SKELETON_HEIGHT_XS;
  const divRef = useRef<HTMLDivElement>(null);
  const [totalHeight, setTotalHeight] = useState(0);
  const gap = SKELETON_GAP_XS;
  const repeatCount = Math.floor(totalHeight / (LOADING_HEIGHT + gap));

  const updateHeight = () => {
    if (divRef.current) {
      setTotalHeight(divRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <div className={cx(styles['loading-container'])}>
      <div className={cx(styles['loading-spinner-container'])}>
        <Loading size='xlarge' color='default' />
      </div>
      <div ref={divRef} className={cx(styles['loading-skeleton-container'])}>
        <LoadingSkeletonList repeatCount={repeatCount} />
      </div>
    </div>
  );
});
LoadingDetails.displayName = 'LoadingDetails';

export const Details: FC<Props> = memo((props) => {
  return (
    <SuspenseErrorBoundary {...props}>
      <Suspense fallback={<LoadingDetails />}>
        <InnerDetails {...props} />
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
Details.displayName = 'Details';
